import React, { useState, useEffect } from "react";
import { Button, Avatar, IconButton, CircularProgress } from "@mui/material";
import { styled } from "@mui/system";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useSnackbar } from "notistack";
import { picBaseUrl } from "../../config/config";
import { Edit_Profile_Settings } from "../../DAL/Edit_Profile/Profile";
import Iconify from "../Iconify";
import { formatString, validateInputs } from "../../utils/constant";
import FormInput from "../GeneralComponents/FormInput";
import { useUserData } from "../../Hooks/App_Context";
import { ImageUpload } from "../../DAL/Login/Login";

const StyledAvatar = styled(Avatar)({
  width: "100px",
  height: "100px",
  marginTop: "10px",
  position: "relative",
});

const requiredStringInputFields = ["first_name", "last_name", "address"];

const EditProfile = ({ handleCloseModel, modelState }) => {
  const { userData = {}, setUserData } = useUserData();
  const [ProfileImage, setProfileImage] = useState();
  const [IsImage, setIsImage] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [Data, setData] = useState({
    first_name: "",
    last_name: "",
    address: "",
    image: null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required input fields
    const errors = validateInputs(Data, requiredStringInputFields, [], []);
    if (Object.keys(errors).length > 0) {
      enqueueSnackbar(formatString(Object.keys(errors)[0]) + " is required", {
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    let imagePath = ProfileImage;

    // Upload image if user selected a new one
    if (Data.image && typeof Data.image !== "string") {
      const formData = new FormData();
      formData.append("image", Data.image);
      const uploadResponse = await ImageUpload(formData);
      if (uploadResponse.code === 200) {
        imagePath = uploadResponse.path;
      } else {
        enqueueSnackbar(uploadResponse.message, { variant: "error" });
        setLoading(false);
        return;
      }
    }

    // Prepare form data for profile update
    const formData = new FormData();
    formData.append("first_name", Data.first_name);
    formData.append("last_name", Data.last_name);
    formData.append("address", Data.address.trim());
    formData.append("profile_image", imagePath);

    // Submit profile update request
    const response = await Edit_Profile_Settings(formData);
    if (response.code === 200) {
      handleCloseModel();
      setIsLoading(false);
      enqueueSnackbar("Admin details updated successfully", {
        variant: "success",
      });

      // Update local user data
      localStorage.setItem("UserData", JSON.stringify(response?.admin));
      setUserData(response?.admin);
    } else {
      setIsLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    // Check if the selected file is an image
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Only image files are allowed.", {
        variant: "error",
      });
      return;
    }
    setIsImage(true);
    setProfileImage(URL.createObjectURL(file)); // Show preview
    setData((prevState) => ({
      ...prevState,
      image: file, // Store the file but do not upload yet
    }));
    setIsImage(false);
  };

  useEffect(() => {
    if (userData) {
      setData((prev) => {
        return {
          ...prev,
          first_name: userData?.first_name,
          last_name: userData?.last_name,
          address: userData?.address,
        };
      });
      setProfileImage(userData?.profile_image);
    }
    // eslint-disable-next-line
  }, [userData]);

  if (!modelState) {
    return null;
  }

  return (
    <div>
      {isLoading ? (
        <>
          <div
            className="d-flex flex-column align-items-center justify-content-center circular_progress"
            style={{ minHeight: "200px" }}
          >
            <CircularProgress />
            <p
              className="mt-2"
              style={{ fontSize: "16px", fontWeight: "500", color: "#555" }}
            >
              Updating profile...
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="col-12 d-flex justify-content-center">
            <div className="Position_update">
              <StyledAvatar
                alt="Profile Image"
                src={
                  ProfileImage
                    ? ProfileImage.startsWith("blob")
                      ? ProfileImage
                      : picBaseUrl + ProfileImage
                    : null
                }
              />

              <input
                accept="image/*"
                style={{ display: "none" }}
                id="upload-button"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="upload-button">
                <IconButton
                  className="Profile-Image-Change"
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                >
                  <CameraAltIcon />
                </IconButton>
              </label>
            </div>
          </div>
          <hr className="me-3" />
          <form onSubmit={handleSubmit} className="form-container">
            <div className="row pe-3">
              <div className="col-12 col-md-6">
                <FormInput
                  label="First Name"
                  fullWidth
                  type="text"
                  value={Data.first_name || ""}
                  sx={false}
                  onChange={(e) =>
                    setData({ ...Data, first_name: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <Iconify
                        icon={"wpf:name"}
                        width={20}
                        height={20}
                        color={"#016699"}
                      />
                    ),
                  }}
                />
              </div>
              <div className="col-12 col-md-6">
                <FormInput
                  label="Last Name"
                  fullWidth
                  type="text"
                  sx={false}
                  value={Data.last_name || ""}
                  onChange={(e) =>
                    setData({ ...Data, last_name: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <Iconify
                        icon={"wpf:name"}
                        width={20}
                        height={20}
                        color={"#016699"}
                      />
                    ),
                  }}
                />
              </div>
              <div className="col-12 mt-2">
                <FormInput
                  label="Address"
                  name="address"
                  required={true}
                  type="text"
                  multline={true}
                  value={Data.address || ""}
                  onChange={(e) =>
                    setData({ ...Data, address: e.target.value })
                  }
                />
              </div>
            </div>
            <hr className="my-3" />
            <div className="d-flex justify-content-end mb-3 mb-lg-0 me-md-0 me-2 px-2">
              <div className="d-flex add-button">
                <Button
                  variant="outlined"
                  onClick={handleCloseModel}
                  className="small-contained-button me-2"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading || IsImage}
                  className="small-contained-button"
                >
                  {loading ? "Please wait..." : "Submit"}
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default EditProfile;
